import { UAParser } from 'ua-parser-js';

/**
 * DiagnosticsCollector - Captures comprehensive client-side diagnostic data for issue reports
 * Based on the old application's Report Issue functionality but enhanced with additional metrics
 */
class DiagnosticsCollector {
    constructor() {
        // Tiered storage for smart retention
        this.consoleLogs = {
            errors: [],      // Tier 1: Keep all errors (critical)
            warnings: [],    // Tier 2: Keep important warnings
            info: []         // Tier 3: Keep recent info/logs
        };
        this.apiCallHistory = {
            errors: [],      // Failed API calls
            successful: []   // Successful calls
        };
        this.jsErrors = [];

        // Tiered limits for smart retention
        // NOTE: Report Issue is used for both bug reports AND usage support requests
        // We need to capture user's recent activity trail to understand what they were doing
        this.MAX_ERRORS = 15;           // Keep up to 15 errors (for bug diagnosis)
        this.MAX_WARNINGS = 20;         // Keep up to 20 warnings
        this.MAX_INFO_LOGS = 100;       // Keep up to 100 info/log messages (USER ACTIVITY TRAIL - most important for support)
        this.MAX_API_ERRORS = 15;       // Keep up to 15 failed API calls
        this.MAX_API_SUCCESSFUL = 30;   // Keep last 30 successful calls (shows what user did recently)
        this.MAX_JS_ERRORS = 10;        // Keep up to 10 JS errors
        this.MAX_LOG_LENGTH = 500;      // Maximum characters per log message
        this.SESSION_START = new Date().toISOString();
        this.initialized = false;
    }

    /**
     * Initialize diagnostic data collection
     * Call this early in the application lifecycle
     */
    initialize() {
        if (this.initialized) return;

        this.interceptConsole();
        this.interceptErrors();
        this.initialized = true;
    }

    /**
     * Intercept console methods to capture logs
     * Similar to old application's console interception
     */
    interceptConsole() {
        const self = this;
        const originalConsole = {
            log: console.log,
            warn: console.warn,
            error: console.error,
            info: console.info
        };

        // Intercept console.log
        console.log = function (...args) {
            self.addConsoleLog('log', args);
            originalConsole.log.apply(console, args);
        };

        // Intercept console.warn
        console.warn = function (...args) {
            self.addConsoleLog('warn', args);
            originalConsole.warn.apply(console, args);
        };

        // Intercept console.error
        console.error = function (...args) {
            self.addConsoleLog('error', args);
            originalConsole.error.apply(console, args);
        };

        // Intercept console.info
        console.info = function (...args) {
            self.addConsoleLog('info', args);
            originalConsole.info.apply(console, args);
        };
    }

    /**
     * Add a console log entry
     */
    addConsoleLog(type, args) {
        try {
            let message = args.map(arg => {
                try {
                    if (typeof arg === 'object' && arg !== null) {
                        // Limit stringified object size
                        const str = JSON.stringify(arg);
                        return str.length > this.MAX_LOG_LENGTH
                            ? str.substring(0, this.MAX_LOG_LENGTH) + '...[TRUNCATED]'
                            : str;
                    }
                    return String(arg);
                } catch (e) {
                    return String(arg);
                }
            }).join(' ');

            // Truncate message if too long
            if (message.length > this.MAX_LOG_LENGTH) {
                message = message.substring(0, this.MAX_LOG_LENGTH) + '...[TRUNCATED]';
            }

            const logEntry = {
                type,
                time: new Date().toISOString(),
                message
            };

            // Tiered storage based on log type
            if (type === 'error') {
                this.consoleLogs.errors.push(logEntry);
                if (this.consoleLogs.errors.length > this.MAX_ERRORS) {
                    this.consoleLogs.errors.shift();
                }
            } else if (type === 'warn') {
                this.consoleLogs.warnings.push(logEntry);
                if (this.consoleLogs.warnings.length > this.MAX_WARNINGS) {
                    this.consoleLogs.warnings.shift();
                }
            } else {
                // info, log, debug
                this.consoleLogs.info.push(logEntry);
                if (this.consoleLogs.info.length > this.MAX_INFO_LOGS) {
                    this.consoleLogs.info.shift();
                }
            }
        } catch (e) {
            // Silently fail to avoid infinite loops
        }
    }

    /**
     * Intercept global JavaScript errors
     */
    interceptErrors() {
        const self = this;

        window.addEventListener('error', (event) => {
            self.addJsError({
                message: event.message,
                source: event.filename,
                line: event.lineno,
                column: event.colno,
                stack: event.error?.stack,
                time: new Date().toISOString()
            });
        });

        window.addEventListener('unhandledrejection', (event) => {
            self.addJsError({
                message: 'Unhandled Promise Rejection: ' + event.reason,
                stack: event.reason?.stack,
                time: new Date().toISOString()
            });
        });
    }

    /**
     * Add a JavaScript error entry
     */
    addJsError(error) {
        this.jsErrors.push(error);
        if (this.jsErrors.length > this.MAX_JS_ERRORS) {
            this.jsErrors.shift();
        }
    }

    /**
     * Add an API call to history (called by Axios interceptor)
     */
    addApiCall(callData) {
        const apiCall = {
            ...callData,
            time: new Date().toISOString()
        };

        // Separate failed and successful API calls
        const isError = callData.status >= 400 || callData.error;

        if (isError) {
            this.apiCallHistory.errors.push(apiCall);
            if (this.apiCallHistory.errors.length > this.MAX_API_ERRORS) {
                this.apiCallHistory.errors.shift();
            }
        } else {
            this.apiCallHistory.successful.push(apiCall);
            if (this.apiCallHistory.successful.length > this.MAX_API_SUCCESSFUL) {
                this.apiCallHistory.successful.shift();
            }
        }
    }

    /**
     * Get browser information using ua-parser-js
     */
    getBrowserInfo() {
        const parser = new UAParser();
        const result = parser.getResult();

        return {
            browser: {
                name: result.browser.name,
                version: result.browser.version
            },
            os: {
                name: result.os.name,
                version: result.os.version
            },
            device: {
                type: result.device.type || 'desktop',
                vendor: result.device.vendor,
                model: result.device.model
            },
            engine: {
                name: result.engine.name,
                version: result.engine.version
            },
            cpu: {
                architecture: result.cpu.architecture
            },
            userAgent: navigator.userAgent
        };
    }

    /**
     * Get performance metrics using Performance API
     */
    getPerformanceMetrics() {
        try {
            const perfData = window.performance.getEntriesByType('navigation')[0];
            const memory = performance.memory;

            return {
                navigation: perfData ? {
                    domContentLoadedTime: Math.round(perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart),
                    loadCompleteTime: Math.round(perfData.loadEventEnd - perfData.loadEventStart),
                    domInteractive: Math.round(perfData.domInteractive),
                    responseTime: Math.round(perfData.responseEnd - perfData.requestStart),
                    transferSize: perfData.transferSize
                } : null,
                memory: memory ? {
                    usedJSHeapSize: Math.round(memory.usedJSHeapSize / 1024 / 1024) + ' MB',
                    totalJSHeapSize: Math.round(memory.totalJSHeapSize / 1024 / 1024) + ' MB',
                    jsHeapSizeLimit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
                } : null,
                timing: {
                    now: Math.round(performance.now()),
                    timeOrigin: performance.timeOrigin
                }
            };
        } catch (e) {
            return { error: 'Performance API not available' };
        }
    }

    /**
     * Get network information
     */
    getNetworkInfo() {
        try {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection) {
                return {
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt,
                    saveData: connection.saveData
                };
            }
            return null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Get localStorage data (with sensitive data redaction)
     */
    getLocalStorage() {
        try {
            const storage = {};
            const sensitiveKeys = ['token', 'password', 'secret', 'key', 'auth'];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                const isSensitive = sensitiveKeys.some(sk => key.toLowerCase().includes(sk));

                if (isSensitive) {
                    storage[key] = '[REDACTED]';
                } else {
                    try {
                        const value = localStorage.getItem(key);
                        // Limit value size to prevent huge storage dumps
                        storage[key] = value && value.length > 200 ? value.substring(0, 200) + '...' : value;
                    } catch (e) {
                        storage[key] = '[ERROR]';
                    }
                }
            }

            return storage;
        } catch (e) {
            return { error: 'localStorage not available' };
        }
    }

    /**
     * Get sessionStorage data (with sensitive data redaction)
     */
    getSessionStorage() {
        try {
            const storage = {};
            const sensitiveKeys = ['token', 'password', 'secret', 'key', 'auth'];

            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                const isSensitive = sensitiveKeys.some(sk => key.toLowerCase().includes(sk));

                if (isSensitive) {
                    storage[key] = '[REDACTED]';
                } else {
                    try {
                        const value = sessionStorage.getItem(key);
                        storage[key] = value && value.length > 200 ? value.substring(0, 200) + '...' : value;
                    } catch (e) {
                        storage[key] = '[ERROR]';
                    }
                }
            }

            return storage;
        } catch (e) {
            return { error: 'sessionStorage not available' };
        }
    }

    /**
     * Get Redux state snapshot (if store is provided)
     */
    getReduxState(store) {
        if (!store) return null;

        try {
            const state = store.getState();
            // Create a sanitized copy
            const sanitized = {};

            Object.keys(state).forEach(key => {
                try {
                    // Redact auth tokens and sensitive data
                    if (key === 'auth' && state[key].token) {
                        sanitized[key] = {
                            ...state[key],
                            token: '[REDACTED]'
                        };
                    } else {
                        sanitized[key] = state[key];
                    }
                } catch (e) {
                    sanitized[key] = '[ERROR]';
                }
            });

            return sanitized;
        } catch (e) {
            return { error: 'Failed to capture Redux state' };
        }
    }

    /**
     * Collect all diagnostic data
     * @param {Object} options - Options for data collection
     * @param {Object} options.store - Redux store instance
     * @param {Object} options.pageState - Current page-specific state (form values, etc.)
     * @param {Object} options.tableData - Table data if applicable
     * @returns {Object} Complete diagnostic data
     */
    collectAll(options = {}) {
        const { store, pageState, tableData } = options;

        // Merge and sort console logs from all tiers chronologically
        const allConsoleLogs = [
            ...this.consoleLogs.errors,
            ...this.consoleLogs.warnings,
            ...this.consoleLogs.info
        ].sort((a, b) => new Date(a.time) - new Date(b.time));

        // Merge API calls chronologically
        const allApiCalls = [
            ...this.apiCallHistory.errors,
            ...this.apiCallHistory.successful
        ].sort((a, b) => new Date(a.time) - new Date(b.time));

        return {
            // Basic metadata
            timestamp: new Date().toISOString(),
            sessionStart: this.SESSION_START,
            sessionDuration: this.getSessionDuration(),
            url: window.location.href,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,

            // Browser information
            browserInfo: this.getBrowserInfo(),

            // Performance metrics
            performance: this.getPerformanceMetrics(),

            // Network information
            network: this.getNetworkInfo(),

            // Console logs (merged from all tiers, sorted chronologically)
            consoleLogs: allConsoleLogs,

            // Log summary for quick overview
            logSummary: {
                totalErrors: this.consoleLogs.errors.length,
                totalWarnings: this.consoleLogs.warnings.length,
                totalInfo: this.consoleLogs.info.length,
                totalLogs: allConsoleLogs.length
            },

            // Recent API calls (merged and sorted)
            apiCallHistory: allApiCalls,

            // API summary
            apiSummary: {
                totalErrors: this.apiCallHistory.errors.length,
                totalSuccessful: this.apiCallHistory.successful.length,
                totalCalls: allApiCalls.length
            },

            // JavaScript errors
            jsErrors: [...this.jsErrors],

            // Storage
            localStorage: this.getLocalStorage(),
            sessionStorage: this.getSessionStorage(),

            // Redux state
            reduxState: this.getReduxState(store),

            // Page-specific data (like old application)
            pageState: pageState || null,
            tableData: tableData || null,

            // Browser capabilities
            capabilities: {
                cookies: navigator.cookieEnabled,
                language: navigator.language,
                languages: navigator.languages,
                online: navigator.onLine,
                platform: navigator.platform,
                doNotTrack: navigator.doNotTrack
            }
        };
    }

    /**
     * Get session duration in human-readable format
     */
    getSessionDuration() {
        const duration = new Date() - new Date(this.SESSION_START);
        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Get the estimated size of diagnostic data in bytes
     */
    getDataSize(diagnostics) {
        try {
            return new Blob([JSON.stringify(diagnostics)]).size;
        } catch (e) {
            return 0;
        }
    }

    /**
     * Clear all collected data
     */
    clear() {
        this.consoleLogs = {
            errors: [],
            warnings: [],
            info: []
        };
        this.apiCallHistory = {
            errors: [],
            successful: []
        };
        this.jsErrors = [];
        this.SESSION_START = new Date().toISOString();
    }
}

// Export singleton instance
const diagnosticsCollector = new DiagnosticsCollector();
export default diagnosticsCollector;
